/**
 * Gestión → Compras (`/gestion/compras`).
 *
 * Puerto de `Compra::New` + `views/menu/compra/new_list.php`: el listado de
 * comprobantes de compra con su proveedor, totales, estado de pago y el flete al
 * que pertenecen, y el detalle de cada uno con sus **productos**, sus **notas de
 * crédito y débito** y los **pagos** que lo afectaron (el original mostraba los
 * productos y las notas; los pagos vivían en otra pantalla).
 *
 * Desde esta pantalla se registran las compras, se **edita el comprobante y las
 * líneas** de una compra ya registrada (con la carga de su **foto**), se **ingresa
 * su stock a almacén**, se paga al proveedor (repartiendo el importe entre varias
 * compras), se crean y anulan las notas de crédito y débito y se anula la compra.
 */

import {
  Ban,
  Eye,
  FileMinus,
  FileText,
  Info,
  Lock,
  Package,
  PackagePlus,
  Pencil,
  Receipt,
  RefreshCw,
  Search,
  Store,
  Truck,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { MenuAcciones } from '@/components/ui/menu-acciones';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatNumber, cn } from '@/lib/utils';

import {
  CompraEditarDialog,
  CompraNuevaDialog,
  CompraNotaDialog,
  CompraPagoDialog,
} from '../components/compras-dialogos';
import { CompraAlmacenDialog } from '../components/compras-almacen-dialog';
import { useAnularCompra, useCompra, useCompras, useProveedoresCompra } from '../compras.api';
import {
  etiquetaEstado,
  fechaCorta,
  hoyIso,
  inicioDeMesIso,
  monto,
  tonoEstado,
} from '../gestion-formato';
import type { Compra, FiltrosCompras } from '../compras.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const TAMANOS = [25, 50, 100];

/** Atajos de periodo del listado (el panel anterior no filtraba por fechas). */
const PERIODOS = [
  { clave: 'mes', label: 'Este mes' },
  { clave: 'ano', label: 'Este año' },
  { clave: 'todo', label: 'Todo' },
] as const;

/** Número con hasta seis decimales (columnas finas del comprobante). */
function numeroFino(valor: number): string {
  return valor.toLocaleString('es-PE', { maximumFractionDigits: 6 });
}

/** Celda de datos del detalle. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-wide text-ink-500 uppercase">{etiqueta}</dt>
      <dd className="text-sm text-ink-800">{valor}</dd>
    </div>
  );
}

/** Detalle de una compra: cabecera, productos, notas y pagos. */
function DetalleCompra({ id, onClose }: { id: number | null; onClose: () => void }) {
  const consulta = useCompra(id);
  const datos = consulta.data;

  return (
    <Dialog
      open={id !== null}
      onClose={onClose}
      size="xl"
      title={datos ? `Compra ${datos.compra.comprobante}` : 'Compra'}
      description={datos ? `${datos.compra.proveedor ?? ''} · ${fechaCorta(datos.compra.fecha_emision)}` : 'Detalle del comprobante'}
      footer={
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo abrir la compra">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {datos ? (
        <div className="space-y-5">
          <dl className="grid gap-4 sm:grid-cols-3">
            <Dato etiqueta="Comprobante" valor={`${datos.compra.tipo_comprobante ?? ''} ${datos.compra.comprobante}`} />
            <Dato etiqueta="Proveedor" valor={`${datos.compra.proveedor ?? '—'} (${datos.compra.ruc ?? '—'})`} />
            <Dato etiqueta="Emisión" valor={fechaCorta(datos.compra.fecha_emision)} />
            <Dato etiqueta="Vencimiento" valor={fechaCorta(datos.compra.fecha_vencimiento)} />
            <Dato etiqueta="Orden de compra" valor={datos.compra.orden_compra ?? '—'} />
            <Dato etiqueta="Guía" valor={datos.compra.guia ?? '—'} />
            <Dato etiqueta="Moneda" valor={`${datos.compra.moneda ?? '—'} ${datos.compra.tipo_cambio ? `· TC ${datos.compra.tipo_cambio}` : ''}`} />
            <Dato etiqueta="Estado" valor={`${etiquetaEstado(datos.compra.estado)} · ${datos.compra.estado_pago ?? '—'}`} />
            <Dato etiqueta="Importe total" valor={formatCurrency(datos.compra.importe_total ?? 0)} />
            <Dato etiqueta="Percepción" valor={formatCurrency(datos.compra.percepcion ?? 0)} />
            <Dato etiqueta="Pagado" valor={formatCurrency(datos.compra.pagado ?? 0)} />
            <Dato etiqueta="Pendiente" valor={formatCurrency(datos.compra.pendiente_pago ?? 0)} />
          </dl>

          {datos.compra.flete ? (
            <section className="rounded-card border border-ink-100 bg-ink-50/40 p-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
                <Truck className="size-4 text-brand-500" aria-hidden="true" />
                Flete {datos.compra.flete.comprobante}
              </h3>
              <dl className="grid gap-4 sm:grid-cols-3">
                <Dato etiqueta="Transportista" valor={`${datos.compra.flete.proveedor ?? '—'} (${datos.compra.flete.ruc ?? '—'})`} />
                <Dato etiqueta="Fecha" valor={fechaCorta(datos.compra.flete.fecha)} />
                <Dato etiqueta="Importe del flete" valor={formatCurrency(datos.compra.flete.importe ?? 0)} />
                <Dato
                  etiqueta="Detracción"
                  valor={datos.compra.flete.detraccion ? formatCurrency(datos.compra.flete.monto_detraccion ?? 0) : 'No aplica'}
                />
                <Dato etiqueta="IGV" valor={datos.compra.flete.exonerado_igv ? 'Exonerado' : 'Afecto'} />
                <Dato etiqueta="Estado del flete" valor={etiquetaEstado(datos.compra.flete.estado ?? 0)} />
              </dl>
            </section>
          ) : null}

          <section>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
              <Package className="size-4 text-brand-500" aria-hidden="true" />
              Productos ({datos.productos.length})
            </h3>
            <div className="max-h-[32vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-3 py-2 whitespace-nowrap">Producto</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Cantidad</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Básico</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">P. unit</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">P. flete</th>
                    <th className="px-3 py-2 text-center whitespace-nowrap">Almacén</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.productos.map((producto) => (
                    <tr key={producto.id} className="border-t border-ink-100">
                      <td className="px-3 py-2">{producto.producto}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatNumber(producto.cantidad)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {numeroFino(producto.basico)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {numeroFino(producto.precio_unidad)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                        {formatCurrency(producto.total)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {formatCurrency(producto.valor_flete)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge variant={producto.almacenado ? 'success' : 'warning'}>
                          {producto.almacenado ? 'Almacenado' : 'Pendiente'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <div>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
                <FileText className="size-4 text-brand-500" aria-hidden="true" />
                Notas de crédito y débito ({datos.notas.length})
              </h3>
              {datos.notas.length === 0 ? (
                <p className="text-sm text-ink-500">Sin notas asociadas.</p>
              ) : (
                <ul className="space-y-2">
                  {datos.notas.map((nota) => (
                    <li
                      key={nota.id}
                      className="rounded-control border border-ink-100 bg-white px-3 py-2 text-sm"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-ink-900">
                          {nota.tipo === 'credito' ? 'Crédito' : 'Débito'} {nota.comprobante}
                        </span>
                        <span className="font-semibold">{formatCurrency(nota.monto)}</span>
                      </div>
                      <p className="text-xs text-ink-500">
                        {fechaCorta(nota.fecha_emision)}
                        {nota.motivo ? ` · ${nota.motivo}` : ''}
                        {nota.aplica_igv ? ' · con IGV' : ' · sin IGV'}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
                <Wallet className="size-4 text-brand-500" aria-hidden="true" />
                Pagos que la afectaron ({datos.pagos.length})
              </h3>
              {datos.pagos.length === 0 ? (
                <p className="text-sm text-ink-500">Sin pagos registrados para esta compra.</p>
              ) : (
                <ul className="space-y-2">
                  {datos.pagos.map((pago) => (
                    <li
                      key={pago.id}
                      className="rounded-control border border-ink-100 bg-white px-3 py-2 text-sm"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-ink-900">
                          Pago #{pago.id} {pago.banco ? `· ${pago.banco}` : ''}
                        </span>
                        <span className="font-semibold">{formatCurrency(pago.monto)}</span>
                      </div>
                      <p className="text-xs text-ink-500">
                        {fechaCorta(pago.fecha)}
                        {pago.referencia ? ` · ${pago.referencia}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}

export function ComprasPage() {
  useDocumentTitle('Gestión · Compras');

  const navigate = useNavigate();

  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [estado, setEstado] = useState('1');
  const [estadoPago, setEstadoPago] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [serie, setSerie] = useState('');
  const [numero, setNumero] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(25);
  const [detalle, setDetalle] = useState<number | null>(null);
  const [nueva, setNueva] = useState(false);
  const [pago, setPago] = useState(false);
  const [notas, setNotas] = useState<{ id: number; total: number } | null>(null);
  const [porAnular, setPorAnular] = useState<Compra | null>(null);
  const [edicion, setEdicion] = useState<Compra | null>(null);
  const [almacen, setAlmacen] = useState<{ id: number; comprobante: string } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const filtros = useMemo<FiltrosCompras>(
    () => ({
      desde,
      hasta,
      estado,
      estado_pago: estadoPago,
      proveedor_id: proveedor ? Number(proveedor) : undefined,
      serie,
      numero,
      search: busqueda.trim(),
      orden: 'fecha',
      dir: 'desc',
      limit: limite,
      offset: pagina * limite,
    }),
    [desde, hasta, estado, estadoPago, proveedor, serie, numero, busqueda, limite, pagina],
  );

  const consulta = useCompras(filtros);
  const proveedores = useProveedoresCompra();
  const anular = useAnularCompra();

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const compras = consulta.data?.compras ?? [];
  const resumen = consulta.data?.resumen;
  const filtrados = consulta.data?.filtrados ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(filtrados / limite));

  const reiniciarPagina = () => setPagina(0);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Compras</h1>
          <p className="mt-1 text-sm text-ink-500">
            Comprobantes de compra con su estado de pago, almacenaje y flete.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Wallet className="size-4" />}
            onClick={() => setPago(true)}
          >
            Pagar al proveedor
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Store className="size-4" />}
            onClick={() => navigate('/gestion/compras/proveedores')}
          >
            Proveedores
          </Button>
          <Button size="sm" leftIcon={<Receipt className="size-4" />} onClick={() => setNueva(true)}>
            Nueva compra
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {resumen ? (
        <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { etiqueta: 'Compras activas', valor: String(resumen.activas), detalle: `${resumen.anuladas} anuladas` },
            { etiqueta: 'Importe total', valor: formatCurrency(resumen.importe), detalle: 'Sobre todas las compras' },
            { etiqueta: 'Pendiente de pago', valor: formatCurrency(resumen.pendiente), detalle: `${resumen.pendientes_pago} comprobantes` },
            { etiqueta: 'Con flete', valor: String(resumen.con_flete), detalle: 'Compras con flete asociado' },
          ].map((tarjeta) => (
            <Card key={tarjeta.etiqueta}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
                <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Desde
              <CampoFecha
               
                className={CONTROL}
                value={desde}
                onChange={(evento) => {
                  setDesde(evento.target.value);
                  reiniciarPagina();
                }}
               tipo="date" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Hasta
              <CampoFecha
               
                className={CONTROL}
                value={hasta}
                onChange={(evento) => {
                  setHasta(evento.target.value);
                  reiniciarPagina();
                }}
               tipo="date" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Proveedor
              <span className="relative block">
              <Select
                className={CONTROL}
                value={proveedor}
                onChange={(evento) => {
                  setProveedor(evento.target.value);
                  reiniciarPagina();
                }}
              >
                <option value="">Todos los proveedores</option>
                {(proveedores.data?.proveedores ?? []).map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.proveedor} ({item.compras})
                  </option>
                ))}
              </Select>
                        </span>
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Estado
              <span className="relative block">
              <Select
                className={CONTROL}
                value={estado}
                onChange={(evento) => {
                  setEstado(evento.target.value);
                  reiniciarPagina();
                }}
              >
                <option value="1">Activas</option>
                <option value="2">Anuladas</option>
                <option value="todos">Todas</option>
              </Select>
                        </span>
            </label>

            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Estado de pago
              <span className="relative block">
              <Select
                className={CONTROL}
                value={estadoPago}
                onChange={(evento) => {
                  setEstadoPago(evento.target.value);
                  reiniciarPagina();
                }}
              >
                <option value="">Pago: todos</option>
                <option value="PENDIENTE">Pendientes de pago</option>
                <option value="PAGADO">Pagadas</option>
              </Select>
                        </span>
            </label>

            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Buscar
              <span className="relative block">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                aria-hidden="true"
              />
              <input
                className={cn(CONTROL, 'pl-9')}
                placeholder="Proveedor, RUC, orden de compra…"
                value={busqueda}
                onChange={(evento) => {
                  setBusqueda(evento.target.value);
                  reiniciarPagina();
                }}
              />
                        </span>
            </label>

            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Serie
              <input
                className={CONTROL}
                placeholder="F001"
                value={serie}
                onChange={(evento) => {
                  setSerie(evento.target.value);
                  reiniciarPagina();
                }}
              />
            </label>

            <label className="flex flex-col gap-1 text-xs text-ink-500">
              Número
              <input
                className={CONTROL}
                placeholder="124"
                value={numero}
                onChange={(evento) => {
                  setNumero(evento.target.value);
                  reiniciarPagina();
                }}
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {PERIODOS.map((opcion) => (
              <button
                key={opcion.clave}
                className="rounded-control border border-ink-200 px-2.5 py-1 text-xs font-semibold text-ink-700 hover:border-brand-300 hover:text-brand-700"
                onClick={() => {
                  if (opcion.clave === 'todo') {
                    setDesde('');
                    setHasta('');
                  } else if (opcion.clave === 'mes') {
                    setDesde(inicioDeMesIso());
                    setHasta(hoyIso());
                  } else {
                    setDesde(`${hoyIso().slice(0, 4)}-01-01`);
                    setHasta(hoyIso());
                  }

                  reiniciarPagina();
                }}
              >
                {opcion.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-ink-500">
              Filas
              <Select
                className="h-9 rounded-control border border-ink-200 bg-white px-2 text-xs text-ink-800"
                value={String(limite)}
                onChange={(evento) => {
                  setLimite(Number(evento.target.value));
                  reiniciarPagina();
                }}
              >
                {TAMANOS.map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {filtrados.toLocaleString('es-PE')} compra{filtrados === 1 ? '' : 's'}
            </span>
          </div>

          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron cargar las compras">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <p className="text-right text-xs text-ink-500">Importes en soles (S/)</p>

          <div className="max-h-[58vh] overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">Comprobante</th>
                  <th className="px-2 py-2">Proveedor</th>
                  <th className="px-2 py-2">Emisión / vence</th>
                  <th className="px-2 py-2 text-right">Importe / pendiente</th>
                  <th className="px-2 py-2">Almacén / flete</th>
                  <th className="px-2 py-2 text-right">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 6 }).map((_, fila) => (
                      <tr key={`esqueleto-${fila}`} className="border-t border-ink-100">
                        {Array.from({ length: 6 }).map((__, columna) => (
                          <td key={`celda-${columna}`} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : null}

                {!consulta.isLoading && compras.length === 0 ? (
                  <tr className="border-t border-ink-100">
                    <td colSpan={6} className="px-3 py-10 text-center text-sm text-ink-500">
                      No hay compras en el rango y filtros elegidos.
                    </td>
                  </tr>
                ) : null}

                {compras.map((compra: Compra) => (
                  <tr key={compra.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                    <td className="px-2 py-2 whitespace-nowrap">
                      <p className="font-semibold text-ink-900">{compra.comprobante}</p>
                      <p className="flex items-center gap-2 text-xs text-ink-500">
                        <span className="max-w-[5.5rem] truncate" title={compra.tipo_comprobante ?? ''}>
                          {compra.tipo_comprobante ?? '—'}
                        </span>
                        <Badge variant={tonoEstado(compra.estado)}>
                          {etiquetaEstado(compra.estado)}
                        </Badge>
                      </p>
                    </td>
                    <td className="max-w-[7.5rem] px-2 py-2">
                      <p className="truncate text-ink-800" title={compra.proveedor ?? ''}>
                        {compra.proveedor ?? '—'}
                      </p>
                      <p className="text-xs text-ink-500">{compra.ruc ?? ''}</p>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <p>{fechaCorta(compra.fecha_emision)}</p>
                      <p className="text-xs text-ink-500">
                        vence {fechaCorta(compra.fecha_vencimiento)}
                      </p>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <p className="font-semibold">
                        {monto(compra.importe_total ?? compra.total_productos)}
                      </p>
                      <p className="text-xs text-ink-500">
                        {(compra.pendiente_pago ?? 0) > 0
                          ? `pend. ${monto(compra.pendiente_pago ?? 0)}`
                          : 'sin pendiente'}
                      </p>
                      <Badge variant={compra.estado_pago === 'PAGADO' ? 'success' : 'warning'}>
                        {compra.estado_pago ?? 'SIN ESTADO'}
                      </Badge>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs text-ink-600">
                      <p>
                        almacén {compra.almacenadas}/{compra.lineas}
                      </p>
                      <p className="text-ink-500">flete {compra.flete_comprobante ?? '—'}</p>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          title="Ver el detalle de la compra"
                          aria-label={`Ver el detalle de la compra ${compra.comprobante}`}
                          onClick={() => setDetalle(compra.id)}
                        >
                          <Eye className="size-4" />
                        </Button>

                        {Number(compra.estado) === 1 ? (
                          <>
                            <Button
                              variant="outline"
                              size="icon"
                              title="Editar el comprobante y las líneas de la compra"
                              aria-label={`Editar la compra ${compra.comprobante}`}
                              onClick={() => setEdicion(compra)}
                            >
                              <Pencil className="size-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              title="Ingreso a almacén"
                              aria-label={`Ingreso a almacén de la compra ${compra.comprobante}`}
                              onClick={() =>
                                setAlmacen({ id: compra.id, comprobante: compra.comprobante })
                              }
                            >
                              <PackagePlus className="size-4" />
                            </Button>
                            <MenuAcciones
                              etiqueta={`Más acciones de la compra ${compra.comprobante}`}
                              acciones={[
                                {
                                  etiqueta: 'Notas de crédito y débito',
                                  icono: <FileMinus className="size-3.5" aria-hidden="true" />,
                                  onClick: () =>
                                    setNotas({ id: compra.id, total: compra.total_productos }),
                                },
                                {
                                  etiqueta: 'Anular la compra',
                                  peligrosa: true,
                                  icono: <Ban className="size-3.5" aria-hidden="true" />,
                                  onClick: () => setPorAnular(compra),
                                },
                              ]}
                            />
                          </>
                        ) : (
                          <Badge variant="neutral">ANULADA</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina + 1} de {totalPaginas.toLocaleString('es-PE')}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina === 0}
                onClick={() => setPagina((actual) => Math.max(0, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina + 1 >= totalPaginas}
                onClick={() => setPagina((actual) => actual + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert variant="info" title="Cómo funciona el registro">
        <ul className="mt-1 ml-5 list-disc space-y-1 text-sm">
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Package className="size-3.5" aria-hidden="true" />
              Al registrar una compra se guardan la cabecera y sus líneas; el <strong>stock no se
              mueve</strong>: se carga después con la entrada de Inventario, que marca la línea como
              almacenada.
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Wallet className="size-3.5" aria-hidden="true" />
              El pago al proveedor se reparte entre varias compras y deja el movimiento bancario de
              «Pago de compras»; se puede adjuntar el comprobante y anularlo después.
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <PackagePlus className="size-3.5" aria-hidden="true" />
              El <strong>ingreso a almacén</strong> carga el stock de la compra línea a línea: cada
              entrada mueve el inventario y marca la línea como almacenada; se puede anular mientras
              no haya nada vendido.
            </span>
          </li>
          <li>
            <span className={ICONO_EN_ALERTA}>
              <Lock className="size-3.5" aria-hidden="true" />
              El lápiz edita el <strong>comprobante y sus líneas</strong> (cantidad, básico, descuento,
              ISC y rodaje) y recalcula los importes con la afectación de IGV y percepción.
            </span>
          </li>
        </ul>
      </Alert>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        El detalle incluye los productos, las notas y los pagos que afectaron a la compra, además
        del flete al que pertenece.
      </p>

      <DetalleCompra id={detalle} onClose={() => setDetalle(null)} />

      <CompraNuevaDialog abierto={nueva} onClose={() => setNueva(false)} onAviso={mostrar} />

      <CompraEditarDialog
        compra={
          edicion
            ? {
                id: edicion.id,
                serie: edicion.serie,
                numero: edicion.numero,
                tipo_comprobante_id: edicion.tipo_comprobante_id ?? undefined,
                proveedor_id: edicion.proveedor_id ?? null,
                fecha_emision: edicion.fecha_emision,
                fecha_vencimiento: edicion.fecha_vencimiento,
                moneda: edicion.moneda,
                tipo_cambio: edicion.tipo_cambio,
                orden_compra: edicion.orden_compra,
                guia: edicion.guia,
                scop: edicion.scop,
                archivo: edicion.archivo ?? null,
              }
            : null
        }
        onClose={() => setEdicion(null)}
        onAviso={mostrar}
      />

      <CompraPagoDialog abierto={pago} onClose={() => setPago(false)} onAviso={mostrar} />

      <CompraAlmacenDialog
        compra={almacen}
        onClose={() => setAlmacen(null)}
        onAviso={mostrar}
      />

      <CompraNotaDialog
        compraId={notas?.id ?? null}
        total={notas?.total ?? 0}
        onClose={() => setNotas(null)}
        onAviso={mostrar}
      />

      <ConfirmDialog
        open={porAnular !== null}
        title={`¿Anular la compra ${porAnular?.comprobante ?? ''}?`}
        description="Una compra anulada deja de contar en los saldos del proveedor. Si ya tiene líneas almacenadas no se puede anular: primero hay que anular esas entradas en Inventario."
        confirmLabel="Anular compra"
        tone="danger"
        loading={anular.isPending}
        onClose={() => setPorAnular(null)}
        onConfirm={() => {
          const compra = porAnular;

          setPorAnular(null);

          if (!compra) return;

          anular.mutate(compra.id, {
            onSuccess: (respuesta) => mostrar(respuesta.mensaje),
            onError: (fallo) =>
              mostrar(fallo instanceof ApiError ? fallo.message : 'No se pudo anular la compra.'),
          });
        }}
      />
    </div>
  );
}
